import type { DashboardActivityKind } from '../types/models';

/** Zeettu-only route prefixes (longest match first). */
const ZEETTU_MODULE_PREFIXES = [
  '/reports/zdr',
  '/delivery-packages',
  '/samples',
  '/zeettu-card-audit',
  '/zeettu',
].sort((a, b) => b.length - a.length);

/** Installment-only route prefixes (longest match first). */
const INSTALLMENT_MODULE_PREFIXES = [
  '/installment-cards',
  '/reports/recovery-dashboard',
  '/reports/irr',
  '/reports/pdr',
  '/audit',
].sort((a, b) => b.length - a.length);

/** Current POS screens (sales, outlets, terminals, stock adjustments); shown while `isPOSEnabled` is false. */
const LEGACY_POS_MODULE_PREFIXES = ['/pos'];
/** Premium POS screens; shown only while `isPOSEnabled` is true. */
const PREMIUM_POS_MODULE_PREFIXES = ['/premium-pos'];

const ZEETTU_ACTIVITY_KINDS = new Set<DashboardActivityKind>(['zdr']);
const INSTALLMENT_ACTIVITY_KINDS = new Set<DashboardActivityKind>(['irr', 'pdr']);

let runtimeZeettuEnabled = true;
let runtimeInstallmentEnabled = true;
let runtimePOSEnabled = true;

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function matchesAnyPrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

export function resolveModuleFlag(value: boolean | undefined): boolean {
  return value !== false;
}

export type ModuleFlags = {
  isZeettuEnabled: boolean;
  isInstallmentEnabled: boolean;
  isPOSEnabled: boolean;
  /** Strict opt-in: only an explicit `true` from settings enables BD features (e.g. bundle analytics). */
  isBDenabled: boolean;
};

let flagsSnapshot: ModuleFlags = {
  isZeettuEnabled: true,
  isInstallmentEnabled: true,
  isPOSEnabled: true,
  isBDenabled: false,
};
const flagListeners = new Set<() => void>();

export function setModuleFlags(config: {
  isZeettuEnabled?: boolean;
  isInstallmentEnabled?: boolean;
  isPOSEnabled?: boolean;
  isBDenabled?: boolean;
}): void {
  runtimeZeettuEnabled = resolveModuleFlag(config.isZeettuEnabled);
  runtimeInstallmentEnabled = resolveModuleFlag(config.isInstallmentEnabled);
  runtimePOSEnabled = resolveModuleFlag(config.isPOSEnabled);
  flagsSnapshot = {
    isZeettuEnabled: runtimeZeettuEnabled,
    isInstallmentEnabled: runtimeInstallmentEnabled,
    isPOSEnabled: runtimePOSEnabled,
    isBDenabled: config.isBDenabled === true,
  };
  flagListeners.forEach((listener) => listener());
}

export function getModuleFlags(): ModuleFlags {
  return flagsSnapshot;
}

/** For `useSyncExternalStore`: notified whenever company settings change the module flags. */
export function subscribeModuleFlags(listener: () => void): () => void {
  flagListeners.add(listener);
  return () => {
    flagListeners.delete(listener);
  };
}

export function isZeettuModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, ZEETTU_MODULE_PREFIXES);
}

export function isInstallmentModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, INSTALLMENT_MODULE_PREFIXES);
}

export function isPOSModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, LEGACY_POS_MODULE_PREFIXES);
}

export function isPremiumPOSModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, PREMIUM_POS_MODULE_PREFIXES);
}

/** False when the path belongs to a disabled module; true for shared paths and enabled modules. */
export function isModuleEnabledForPath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  if (isZeettuModulePath(path) && !runtimeZeettuEnabled) {
    return false;
  }
  if (isInstallmentModulePath(path) && !runtimeInstallmentEnabled) {
    return false;
  }
  if (isPOSModulePath(path) && runtimePOSEnabled) {
    return false;
  }
  if (isPremiumPOSModulePath(path) && !runtimePOSEnabled) {
    return false;
  }
  return true;
}

/** Dashboard recent-activity feed: filter report rows by module flags. */
export function isDashboardActivityKindEnabled(kind: DashboardActivityKind): boolean {
  if (ZEETTU_ACTIVITY_KINDS.has(kind)) {
    return runtimeZeettuEnabled;
  }
  if (INSTALLMENT_ACTIVITY_KINDS.has(kind)) {
    return runtimeInstallmentEnabled;
  }
  return true;
}
